import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './SetVariablesForm.module.scss';
import { useFormikContext } from 'formik';
import MultiSelectList from 'component/atom/forms/select/multi-select-list/MultiSelectList';
import EntityItem from '../capture/components/entity-item/EntityItem';
import Button from 'component/atom/forms/button/Button';
import Input from 'component/atom/forms/input/input/Input';

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const SetVariablesForm = ({ data, isFetching }) => {
  const formik = useFormikContext();
  const [openIndex, setOpenIndex] = useState(null);
  const containerRef = useRef(null);

  const radioOptions = [
    { label: 'Entire User Reply', value: '0' },
    { label: 'Entities', value: '1' },
  ];

  const items = data?.data?.items || [];

  const typeOptions =
    data?.data?.items?.map((item) => ({
      value: toNum(item.variableId),
      label: item.variableName,
    })) || [];

  const entities = useMemo(
    () => formik.values?.suggestedEntityQuestions || [],
    [formik.values?.suggestedEntityQuestions],
  );

  const selectedIds = entities
    .map((q) => toNum(q.variableId))
    .filter(Number.isInteger);

  const selectedVariableLabel = useMemo(() => {
    if (!items?.length) return '';
    const match = items.find(
      (item) => item.variableId == formik.values.variableId,
    );
    return match?.variableName || '';
  }, [items, formik.values.variableId]);

  useEffect(() => {
    if ((formik.values.variableLabel || '') !== (selectedVariableLabel || '')) {
      formik.setFieldValue('variableLabel', selectedVariableLabel || '');
    }
  }, [selectedVariableLabel, formik.values.variableLabel]);

  useEffect(() => {
    if (formik.values.isEntities && formik.values.selectedTab !== '1') {
      formik.setFieldValue('selectedTab', '1');
    }
    if (!formik.values.isEntities && formik.values.selectedTab !== '0') {
      formik.setFieldValue('selectedTab', '0');
    }
  }, [formik.values.isEntities, formik.values.selectedTab]);

  const handleVariablesChange = (e) => {
    const rawValues = Array.isArray(e)
      ? e
      : (e?.target?.value ?? e?.value ?? []);

    const values = (rawValues || [])
      .map((v) => toNum(v))
      .filter(Number.isInteger);

    const current = formik.values?.suggestedEntityQuestions || [];

    let next = current.filter((q) => values.includes(toNum(q.variableId)));

    const added = values.filter(
      (id) => !current.some((q) => toNum(q.variableId) === id),
    );

    if (added.length > 0) {
      const maxOrder =
        next.length > 0 ? Math.max(...next.map((q) => q.orderNo || 0)) : 0;

      const newItems = added.map((id, idx) => ({
        variableId: id,
        question: '',
        validation_ErrorMessage: '',
        orderNo: maxOrder + idx + 1,
        isLookupEnabled: false,
        validationRule: '',
        lookups: [],
      }));

      next = [...next, ...newItems];
    }

    formik.setFieldValue('suggestedEntityQuestions', next);
    if (next.length === 0) setOpenIndex(null);
  };

  const sortedEntities = useMemo(() => {
    return [...entities].sort((a, b) => (a.orderNo || 0) - (b.orderNo || 0));
  }, [entities]);

  return (
    <form
      className={styles['capture-form']}
      onSubmit={formik.handleSubmit}
      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
    >
      <MultiSelectList
        label='Variable'
        placeholder='Select Variables'
        name='entityVariables'
        options={typeOptions}
        value={selectedIds}
        onChange={handleVariablesChange}
        showTags={false}
      />
      <div ref={containerRef} className={styles['entity-list']}>
        {sortedEntities.map((item) => {
          const actualIndex = entities.findIndex(
            (q) => toNum(q.variableId) === toNum(item.variableId),
          );

          const label =
            typeOptions.find((o) => toNum(o.value) === toNum(item.variableId))
              ?.label || item.variableId;

          return (
            <div className={styles['entity-item']}>
              <p
                className={styles['entity-label']}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {label}
              </p>
              <div className={styles['action-container']}>
                <Input />
                <Button
                  type='delete'
                  variant='danger'
                  onClick={(e) => {
                    e.stopPropagation();
                    const next = formik.values.suggestedEntityQuestions.filter(
                      (_, i) => i !== actualIndex,
                    );
                    formik.setFieldValue('suggestedEntityQuestions', next);
                  }}
                  className={styles['remove-button']}
                />
              </div>
            </div>
          );
        })}
      </div>
    </form>
  );
};

export default SetVariablesForm;
